import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Search, Building2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });

// Admin-only: change the franchise (customer / destination) on a FINALIZED
// invoice or DC. Search by Franchise ID → fetch name → update → audit log.
export const FranchiseChangeDialog = ({ entity, open, onOpenChange }) => {
  const isInvoice = entity === 'invoice';
  const numberLabel = isInvoice ? 'Invoice Number' : 'DC Number';
  const [docNo, setDocNo] = React.useState('');
  const [doc, setDoc] = React.useState(null);
  const [query, setQuery] = React.useState('');
  const [suggestions, setSuggestions] = React.useState([]);
  const [picked, setPicked] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [logs, setLogs] = React.useState([]);

  const currentName = doc ? (isInvoice ? doc.customer_name : doc.to_location) : '';

  const fetchLogs = React.useCallback((refNumber) => {
    const p = new URLSearchParams({ entity_type: entity });
    if (refNumber) p.set('ref', refNumber);
    axios.get(API + '/accounts/franchise-change-log?' + p, auth())
      .then((r) => setLogs(r.data.history || [])).catch(() => {});
  }, [entity]);

  React.useEffect(() => { if (open) fetchLogs(''); }, [open, fetchLogs]);

  const lookup = () => {
    const path = isInvoice ? '/accounts/invoice-lookup/' : '/accounts/dc-lookup/';
    axios.get(API + path + docNo.trim(), auth())
      .then((r) => {
        const d = isInvoice ? r.data.invoice : r.data.dc;
        if (isInvoice && !['finalized', 'partially_paid', 'paid'].includes(d.status)) {
          return toast.error('Franchise can only be changed on FINALIZED invoices (status: ' + d.status + ')');
        }
        if (!isInvoice && d.status === 'draft') {
          return toast.error('Draft DCs are edited via the normal workflow');
        }
        setDoc(d);
        fetchLogs(isInvoice ? d.invoice_number : d.dc_number);
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Not found'));
  };

  React.useEffect(() => {
    const q = query.trim();
    if (!q || picked) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      axios.get(API + '/accounts/franchise-lookup?q=' + encodeURIComponent(q), auth())
        .then((r) => setSuggestions(r.data.results || [])).catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query, picked]);

  const update = () => {
    if (!doc || !picked) return;
    setBusy(true);
    const path = isInvoice
      ? `/accounts/invoices/${doc.id}/change-franchise`
      : `/accounts/delivery-challans/${doc.id}/change-franchise`;
    axios.post(API + path, { franchise_ref: picked.franchise_id || picked.id }, auth())
      .then(() => {
        toast.success('Franchise updated to ' + picked.name);
        const refNo = isInvoice ? doc.invoice_number : doc.dc_number;
        setDoc(null); setDocNo(''); setPicked(null); setQuery('');
        fetchLogs(refNo);
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Update failed'))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="franchise-change-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600" />
            Change Franchise — {isInvoice ? 'Finalized Invoice' : 'Finalized Delivery Challan'}
          </DialogTitle>
          <DialogDescription>Admin only. Search the new franchise by Franchise ID; every change is audit-logged.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input placeholder={numberLabel} value={docNo} onChange={(e) => setDocNo(e.target.value)} data-testid="fc-doc-input" />
          <Button onClick={lookup} data-testid="fc-load-btn"><Search className="w-4 h-4 mr-1" />Load</Button>
        </div>

        {doc && (
          <div className="space-y-3">
            <div className="text-sm bg-slate-50 border rounded-md p-3" data-testid="fc-current-franchise">
              <span className="text-slate-500">Current Franchise:</span>{' '}
              <span className="font-semibold">{currentName || '—'}</span>
              <span className="text-slate-400 ml-2">({isInvoice ? doc.invoice_number : doc.dc_number})</span>
            </div>

            <div className="relative">
              <Input
                placeholder="Search new franchise by Franchise ID (e.g. FRN-…) or name"
                value={picked ? `${picked.name} · ${picked.franchise_id || ''}` : query}
                onChange={(e) => { setPicked(null); setQuery(e.target.value); }}
                data-testid="fc-franchise-search"
              />
              {!picked && suggestions.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-56 overflow-y-auto" data-testid="fc-suggestions">
                  {suggestions.map((s) => (
                    <button key={s.id} type="button"
                      onMouseDown={(e) => { e.preventDefault(); setPicked(s); setSuggestions([]); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b last:border-0"
                      data-testid={'fc-option-' + (s.franchise_id || s.id)}>
                      <span className="font-mono text-xs text-indigo-600 mr-2">{s.franchise_id}</span>
                      <span className="font-medium">{s.name}</span>
                      {(s.district || s.state) && <span className="text-xs text-slate-400 ml-2">{[s.district, s.state].filter(Boolean).join(', ')}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {picked && (
              <div className="flex items-center gap-3 text-sm bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="fc-preview">
                <span className="font-medium text-slate-700">{currentName || '—'}</span>
                <ArrowRight className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="font-semibold text-emerald-800">{picked.name}</span>
                <span className="font-mono text-xs text-emerald-700">({picked.franchise_id})</span>
              </div>
            )}

            <Button onClick={update} disabled={!picked || busy} data-testid="fc-update-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Franchise'}
            </Button>
          </div>
        )}

        <div className="pt-2">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Franchise Change Log</h3>
          {logs.length === 0 ? (
            <p className="text-xs text-slate-400 italic" data-testid="fc-log-empty">No franchise changes recorded yet.</p>
          ) : (
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full text-xs" data-testid="fc-log-table">
                <thead><tr className="bg-slate-50 text-left">
                  <th className="p-2">{isInvoice ? 'Invoice' : 'DC'}</th>
                  <th className="p-2">Previous Franchise</th><th className="p-2">New Franchise</th>
                  <th className="p-2">Franchise ID</th><th className="p-2">Changed By</th><th className="p-2">Date & Time</th>
                </tr></thead>
                <tbody>{logs.map((l) => (
                  <tr key={l.id} className="border-t" data-testid="fc-log-row">
                    <td className="p-2 font-mono">{l.ref_number}</td>
                    <td className="p-2">{l.previous_franchise_name || '—'}</td>
                    <td className="p-2 font-semibold">{l.new_franchise_name}</td>
                    <td className="p-2 font-mono">{l.franchise_id}</td>
                    <td className="p-2">{l.changed_by_name}</td>
                    <td className="p-2">{(l.changed_at || '').slice(0, 19).replace('T', ' ')}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FranchiseChangeDialog;
